import { useEffect, useState, useRef } from "react";
import { api, fileUrl } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, ChevronUp, ChevronDown, Save, Monitor, Smartphone, Upload } from "lucide-react";

const SECTION_TYPES = [
  { id: "hero", name: "Hero", icon: "🎯" },
  { id: "features", name: "Features", icon: "✨" },
  { id: "gallery", name: "Gallery", icon: "🖼" },
  { id: "testimonials", name: "Testimonials", icon: "💬" },
  { id: "cta", name: "CTA", icon: "🚀" },
  { id: "faq", name: "FAQ", icon: "❓" },
  { id: "contact", name: "Contact", icon: "📞" },
];

export default function VisualBuilder({ website }) {
  const [sections, setSections] = useState([]);
  const [selected, setSelected] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get(`/websites/${website.website_id}/builder`).then(r => setSections(r.data.sections || []));
  }, [website.website_id]);

  const save = async () => {
    await api.put(`/websites/${website.website_id}/builder`, { sections });
    toast.success("Builder saved");
    setDirty(false);
  };

  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= sections.length) return;
    const arr = [...sections]; [arr[i], arr[j]] = [arr[j], arr[i]];
    setSections(arr); setDirty(true);
  };

  const remove = (i) => {
    setSections(sections.filter((_, idx) => idx !== i));
    if (selected === i) setSelected(null);
    setDirty(true);
  };

  const add = (type) => {
    const templates = {
      hero: { type: "hero", title: "New Hero", subtitle: "Your subtitle", cta: "Learn More", image_url: "" },
      features: { type: "features", title: "Features", items: [{ title: "Feature 1", desc: "Description" }, { title: "Feature 2", desc: "Description" }, { title: "Feature 3", desc: "Description" }] },
      gallery: { type: "gallery", title: "Gallery", items: [] },
      testimonials: { type: "testimonials", title: "What people say", items: [{ name: "Rahim", role: "Customer", quote: "Amazing!" }] },
      cta: { type: "cta", title: "Ready to get started?", cta: "Contact Us", link: "" },
      faq: { type: "faq", title: "FAQ", items: [{ q: "How to start?", a: "It's simple" }] },
      contact: { type: "contact", title: "Contact Us", email: "", phone: "", address: "" },
    };
    setSections([...sections, { id: `s${Date.now()}`, ...templates[type] }]);
    setDirty(true);
  };

  const update = (i, patch) => {
    setSections(sections.map((s, idx) => idx === i ? { ...s, ...patch } : s));
    setDirty(true);
  };

  return (
    <div className="grid lg:grid-cols-12 gap-4">
      {/* Sidebar - sections list */}
      <div className="lg:col-span-3 space-y-3">
        <div className="bg-white p-3 rounded-xl border sticky top-24">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-bold text-sm">Sections</h4>
            <Button size="sm" onClick={save} disabled={!dirty} className="h-7 text-xs bg-sky-500 hover:bg-sky-600" data-testid="builder-save"><Save className="w-3 h-3 mr-1"/>Save</Button>
          </div>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {sections.map((s, i) => (
              <div key={s.id} className={`p-2 rounded-md border cursor-pointer flex items-center justify-between ${selected === i ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`} onClick={() => setSelected(i)} data-testid={`builder-section-${i}`}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-xs">{SECTION_TYPES.find(t => t.id === s.type)?.icon || "📄"}</span>
                  <span className="text-xs font-semibold truncate">{s.title || s.type}</span>
                </div>
                <div className="flex gap-0.5">
                  <button onClick={e => { e.stopPropagation(); move(i, -1); }} className="w-5 h-5 hover:bg-slate-200 rounded"><ChevronUp className="w-3 h-3 mx-auto"/></button>
                  <button onClick={e => { e.stopPropagation(); move(i, 1); }} className="w-5 h-5 hover:bg-slate-200 rounded"><ChevronDown className="w-3 h-3 mx-auto"/></button>
                  <button onClick={e => { e.stopPropagation(); remove(i); }} className="w-5 h-5 hover:bg-rose-100 text-rose-500 rounded"><Trash2 className="w-3 h-3 mx-auto"/></button>
                </div>
              </div>
            ))}
            {sections.length === 0 && <div className="text-xs text-slate-400 text-center py-4">No sections yet</div>}
          </div>
          <div className="mt-3 pt-3 border-t">
            <div className="text-xs font-bold mb-2">Add Section</div>
            <div className="grid grid-cols-2 gap-1">
              {SECTION_TYPES.map(t => (
                <button key={t.id} onClick={() => add(t.id)} className="p-2 text-xs rounded-md border hover:border-sky-400 hover:bg-sky-50" data-testid={`add-${t.id}`}>
                  {t.icon} {t.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Editor panel */}
      <div className="lg:col-span-4">
        <div className="bg-white p-4 rounded-xl border">
          <h4 className="font-bold mb-3">Edit</h4>
          {selected === null || !sections[selected] ? (
            <div className="text-sm text-slate-400 text-center py-8">Select a section from the left to edit</div>
          ) : (
            <SectionEditor section={sections[selected]} onChange={p => update(selected, p)}/>
          )}
        </div>
      </div>

      {/* Live Preview */}
      <div className="lg:col-span-5">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-bold text-sm">Live Preview</h4>
          <div className="flex gap-1">
            <button onClick={() => setDevice("desktop")} className={`p-1.5 rounded ${device === "desktop" ? "bg-sky-100 text-sky-600" : "text-slate-400 hover:bg-slate-100"}`} data-testid="preview-desktop"><Monitor className="w-4 h-4"/></button>
            <button onClick={() => setDevice("mobile")} className={`p-1.5 rounded ${device === "mobile" ? "bg-sky-100 text-sky-600" : "text-slate-400 hover:bg-slate-100"}`} data-testid="preview-mobile"><Smartphone className="w-4 h-4"/></button>
          </div>
        </div>
        <div className={`bg-slate-100 rounded-xl border overflow-hidden mx-auto transition-all ${device === "mobile" ? "max-w-[390px]" : "w-full"}`}>
          <div className="bg-slate-800 text-white text-[10px] px-3 py-1.5 flex items-center gap-2">
            <div className="flex gap-1">
              <div className="w-2 h-2 rounded-full bg-rose-500"/>
              <div className="w-2 h-2 rounded-full bg-amber-500"/>
              <div className="w-2 h-2 rounded-full bg-emerald-500"/>
            </div>
            <div className="flex-1 text-center opacity-70 font-mono">{website.subdomain}</div>
          </div>
          <div className="bg-white max-h-[600px] overflow-y-auto">
            <SectionsPreview sections={sections} website={website}/>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionEditor({ section, onChange }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const upload = async (file, key = "image_url") => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ [key]: r.data.path });
    } finally { setUploading(false); }
  };

  const updateItem = (i, patch) => {
    const items = (section.items || []).map((it, idx) => idx === i ? { ...it, ...patch } : it);
    onChange({ items });
  };
  const addItem = () => {
    const defaults = { features: { title: "New", desc: "" }, testimonials: { name: "", role: "", quote: "" }, faq: { q: "", a: "" }, gallery: { image_url: "", title: "" } };
    onChange({ items: [...(section.items || []), defaults[section.type] || {}] });
  };
  const delItem = i => onChange({ items: section.items.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-2">
      <Badge variant="outline" className="uppercase text-[10px]">{section.type}</Badge>
      <div>
        <label className="text-xs font-semibold">Title</label>
        <Input value={section.title || ""} onChange={e => onChange({ title: e.target.value })} className="h-8 text-sm" data-testid="edit-title"/>
      </div>
      {section.type === "hero" && (
        <>
          <div>
            <label className="text-xs font-semibold">Subtitle</label>
            <Textarea value={section.subtitle || ""} onChange={e => onChange({ subtitle: e.target.value })} rows={2} className="text-sm"/>
          </div>
          <div>
            <label className="text-xs font-semibold">CTA Text</label>
            <Input value={section.cta || ""} onChange={e => onChange({ cta: e.target.value })} className="h-8 text-sm"/>
          </div>
          <div>
            <label className="text-xs font-semibold">Image</label>
            <input ref={fileRef} type="file" hidden accept="image/*" onChange={e => upload(e.target.files[0])}/>
            <div className="flex items-center gap-2 mt-1">
              {section.image_url && <img src={fileUrl(section.image_url)} className="w-12 h-12 rounded object-cover border" alt=""/>}
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}><Upload className="w-3 h-3 mr-1"/>{uploading ? "..." : "Upload"}</Button>
            </div>
          </div>
        </>
      )}
      {section.type === "cta" && (
        <>
          <div><label className="text-xs font-semibold">Button</label><Input value={section.cta || ""} onChange={e => onChange({ cta: e.target.value })} className="h-8 text-sm"/></div>
          <div><label className="text-xs font-semibold">Link</label><Input value={section.link || ""} onChange={e => onChange({ link: e.target.value })} className="h-8 text-sm"/></div>
        </>
      )}
      {(section.type === "features" || section.type === "testimonials" || section.type === "faq" || section.type === "gallery") && (
        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold">Items ({section.items?.length || 0})</label>
            <Button size="sm" variant="outline" onClick={addItem} className="h-6 text-xs"><Plus className="w-3 h-3"/></Button>
          </div>
          <div className="space-y-2 mt-2 max-h-96 overflow-y-auto">
            {(section.items || []).map((it, i) => (
              <div key={i} className="p-2 rounded-lg border bg-slate-50 space-y-1">
                <div className="flex justify-between">
                  <span className="text-[10px] font-mono text-slate-400">#{i}</span>
                  <button onClick={() => delItem(i)} className="text-rose-500"><Trash2 className="w-3 h-3"/></button>
                </div>
                {section.type === "features" && (<>
                  <Input value={it.title || ""} onChange={e => updateItem(i, { title: e.target.value })} placeholder="Title" className="h-7 text-xs"/>
                  <Textarea value={it.desc || ""} onChange={e => updateItem(i, { desc: e.target.value })} placeholder="Description" rows={2} className="text-xs"/>
                </>)}
                {section.type === "testimonials" && (<>
                  <Input value={it.name || ""} onChange={e => updateItem(i, { name: e.target.value })} placeholder="Name" className="h-7 text-xs"/>
                  <Input value={it.role || ""} onChange={e => updateItem(i, { role: e.target.value })} placeholder="Role" className="h-7 text-xs"/>
                  <Textarea value={it.quote || ""} onChange={e => updateItem(i, { quote: e.target.value })} placeholder="Quote" rows={2} className="text-xs"/>
                </>)}
                {section.type === "faq" && (<>
                  <Input value={it.q || ""} onChange={e => updateItem(i, { q: e.target.value })} placeholder="Question" className="h-7 text-xs"/>
                  <Textarea value={it.a || ""} onChange={e => updateItem(i, { a: e.target.value })} placeholder="Answer" rows={2} className="text-xs"/>
                </>)}
                {section.type === "gallery" && (<>
                  <Input value={it.title || ""} onChange={e => updateItem(i, { title: e.target.value })} placeholder="Caption" className="h-7 text-xs"/>
                  <input type="file" accept="image/*" hidden id={`gal-${i}`} onChange={async e => {
                    const fd = new FormData(); fd.append("file", e.target.files[0]);
                    const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    updateItem(i, { image_url: r.data.path });
                  }}/>
                  <div className="flex items-center gap-2">
                    {it.image_url && <img src={fileUrl(it.image_url)} className="w-10 h-10 rounded object-cover"/>}
                    <label htmlFor={`gal-${i}`}><Button size="sm" variant="outline" asChild className="h-6 text-xs"><span><Upload className="w-3 h-3 mr-1"/>Image</span></Button></label>
                  </div>
                </>)}
              </div>
            ))}
          </div>
        </div>
      )}
      {section.type === "contact" && (
        <>
          <div><label className="text-xs font-semibold">Email</label><Input value={section.email || ""} onChange={e => onChange({ email: e.target.value })} className="h-8 text-sm"/></div>
          <div><label className="text-xs font-semibold">Phone</label><Input value={section.phone || ""} onChange={e => onChange({ phone: e.target.value })} className="h-8 text-sm"/></div>
          <div><label className="text-xs font-semibold">Address</label><Input value={section.address || ""} onChange={e => onChange({ address: e.target.value })} className="h-8 text-sm"/></div>
        </>
      )}
    </div>
  );
}

function SectionsPreview({ sections, website }) {
  const brand = website.primary_color || "#0EA5E9";
  return (
    <div className="text-sm">
      {sections.map(s => <SectionRender key={s.id} s={s} brand={brand} website={website}/>)}
    </div>
  );
}

function SectionRender({ s, brand, website }) {
  switch (s.type) {
    case "hero":
      return (
        <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${brand}, #0F172A)` }}>
          <div className="px-6 py-12 text-white text-center">
            <h1 className="text-2xl sm:text-4xl font-black leading-tight font-bn">{s.title}</h1>
            {s.subtitle && <p className="mt-3 opacity-90 font-bn">{s.subtitle}</p>}
            {s.cta && <button className="mt-5 px-5 py-2 bg-white rounded-full font-bold" style={{ color: brand }}>{s.cta}</button>}
          </div>
          {s.image_url && <img src={fileUrl(s.image_url)} className="w-full h-40 object-cover" alt=""/>}
        </div>
      );
    case "features":
      return (
        <section className="px-6 py-10 bg-slate-50">
          <h2 className="text-xl font-black text-center font-bn">{s.title}</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            {(s.items || []).map((it, i) => (
              <div key={i} className="p-4 bg-white rounded-lg border">
                <div className="w-8 h-8 rounded-lg mb-2" style={{ background: brand }}/>
                <div className="font-bold text-sm">{it.title}</div>
                <div className="text-xs text-slate-500 mt-1">{it.desc}</div>
              </div>
            ))}
          </div>
        </section>
      );
    case "gallery":
      return (
        <section className="px-6 py-10">
          <h2 className="text-xl font-black text-center font-bn">{s.title}</h2>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {(s.items || []).map((it, i) => (
              <div key={i} className="aspect-square bg-slate-100 rounded-lg overflow-hidden">
                {it.image_url && <img src={fileUrl(it.image_url)} className="w-full h-full object-cover" alt=""/>}
              </div>
            ))}
          </div>
        </section>
      );
    case "testimonials":
      return (
        <section className="px-6 py-10 bg-slate-50">
          <h2 className="text-xl font-black text-center font-bn">{s.title}</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {(s.items || []).map((it, i) => (
              <div key={i} className="p-4 bg-white rounded-lg border">
                <div className="text-xs italic text-slate-600 font-bn">"{it.quote}"</div>
                <div className="mt-2 text-xs font-bold">{it.name} <span className="text-slate-400 font-normal">— {it.role}</span></div>
              </div>
            ))}
          </div>
        </section>
      );
    case "cta":
      return (
        <section className="px-6 py-12 text-center text-white" style={{ background: brand }}>
          <h2 className="text-xl font-black font-bn">{s.title}</h2>
          {s.cta && <a href={s.link || "#"} className="mt-4 inline-block px-6 py-2 bg-white rounded-full font-bold" style={{ color: brand }}>{s.cta}</a>}
        </section>
      );
    case "faq":
      return (
        <section className="px-6 py-10">
          <h2 className="text-xl font-black text-center font-bn">{s.title}</h2>
          <div className="mt-4 space-y-2">
            {(s.items || []).map((it, i) => (
              <details key={i} className="p-3 bg-slate-50 rounded-lg border">
                <summary className="font-bold text-sm font-bn cursor-pointer">{it.q}</summary>
                <p className="mt-2 text-xs text-slate-600 font-bn">{it.a}</p>
              </details>
            ))}
          </div>
        </section>
      );
    case "contact":
      return (
        <section className="px-6 py-10 text-center">
          <h2 className="text-xl font-black font-bn">{s.title}</h2>
          <div className="mt-4 text-xs text-slate-600 space-y-1">
            {s.email && <div>📧 {s.email}</div>}
            {s.phone && <div>📞 {s.phone}</div>}
            {s.address && <div className="font-bn">📍 {s.address}</div>}
          </div>
        </section>
      );
    default:
      return null;
  }
}
